import { TypographyMuted } from "@/components/typography/Muted";
import Drawer from "@/components/ui/Drawer";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import MDEditor from "@uiw/react-md-editor";
import { Buffer } from "buffer";
import { NotebookTabs } from "lucide-react";
import { useEffect, useState } from "react";
import ReleaseNotesModal from "./ReleaseNotesModal";

type Props = {
  isOpen: boolean;
  setIsOpen: () => void;
  releaseNotesBase64: string | undefined;
  userGuideBase64: string | undefined;
};

const AppFilesDrawer = ({
  isOpen,
  setIsOpen,
  releaseNotesBase64,
  userGuideBase64
}: Props) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const releaseNotes = decodeBase64(releaseNotesBase64);
  const userGuide = decodeBase64(userGuideBase64);

  useEffect(() => {
    const darkModeClass = document.documentElement.classList.contains("dark");
    setIsDarkMode(darkModeClass);
    const observer = new MutationObserver(() => {
      const darkModeClass2 =
        document.documentElement.classList.contains("dark");
      setIsDarkMode(darkModeClass2);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="min-w-96" data-color-mode={isDarkMode ? "dark" : "light"}>
        {releaseNotes ? (
          <ReleaseNotesModal
            releaseNotes={releaseNotes}
            isDarkMode={isDarkMode}
          >
            <Button className="flex items-center gap-2" size="sm">
              <NotebookTabs size={14} /> Release Notes
            </Button>
          </ReleaseNotesModal>
        ) : (
          <TypographyMuted className="min-w-max bg-brand-gray-50 dark:bg-brand-gray-800 border border-brand-gray-100 dark:border-brand-gray-600 rounded-md flex justify-between items-center gap-4 py-2.5 px-4 mt-4">
            There are no release notes for this app.
          </TypographyMuted>
        )}
        {userGuide ? (
          <MDEditor.Markdown
            className={cn("p-1 mt-10", isDarkMode && "bg-brand-gray-900")}
            source={userGuide}
          />
        ) : (
          <>
            <Separator className="my-6" />
            <TypographyMuted className="px-4">
              There is no documentation for this app.
            </TypographyMuted>
          </>
        )}
      </div>
    </Drawer>
  );
};

function decodeBase64(string: string | undefined) {
  if (!string) return undefined;
  return Buffer.from(string, "base64").toString();
}

export default AppFilesDrawer;
